import React, { useEffect, useMemo, useState } from "react";
import {
    ColumnDef,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { ErrorType } from "@/types";
import { SaleTax, SaleTaxTable } from "@/types/system-setting/sale-tax";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { displayValue } from "@/lib/helper";
import { TaxGroup } from "@/types/system-setting/tax-group";
import { Status } from "@/types/enums";
import TaxGroupForm from "../modal/form";
import { DataTableRowActions } from "./data-table-row-actions";
import { useDeleteTaxGroupMutation, useGetSalesTaxQuery, useGetTaxGroupQuery } from "@/services/salesTaxApi";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import TableFilters from "@/components/ui/table/data-table-filter";

const initialValues = {
    limit: 10,
    offset: 0,
    sort_order: "desc",
    sort_key: "id",
    type: "tax_group"
}

export default function TaxGroupTableView() {
    const tax_group = (() => {
        try {
            return (
                JSON.parse(localStorage.getItem("accessLevels") as string).tax_group ??
                "no_access"
            );
        } catch {
            return "no_access";
        }
    })();

    const orgId =
        useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [data, setData] = useState<TaxGroup | undefined>(undefined);
    const [action, setAction] = useState<'add' | 'edit'>('add');
    const [openFilter, setOpenFilter] = useState(false);
    const [filterData, setFilterData] = useState<Record<string, any>>({});

    const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } = useTableData(initialValues);

    const { data: taxGroupData, isLoading, refetch } = useGetTaxGroupQuery({ query: query, org_id: orgId });

    const { data: salesTaxData, isLoading: salesTaxLoading } = useGetSalesTaxQuery({ query: "status=active", org_id: orgId });

    const [deleteTaxGroup, { isLoading: deleteLoading }] = useDeleteTaxGroupMutation();
    
    const { toast } = useToast();
    
    const taxGrouptableData = useMemo(() => {
        return Array.isArray(taxGroupData?.data)
            ? (taxGroupData?.data)
            : [];
    }, [taxGroupData]);

    const salesTaxOptions = useMemo(() => {
        return Array.isArray(salesTaxData?.data)
            ? (salesTaxData?.data?.map((item: SaleTax) => ({
                value: item.id,
                label: `${item.name} (${item.percentage}%)`,
            })))
            : [];
    }, [salesTaxData]);

    const handleEditTaxGroup = (data: TaxGroup) => {
        setData(data);
        setAction("edit");
        setIsDialogOpen(true);
    };

    const handleDeleteTaxGroup = async (data: TaxGroup) => {
        try {
            if (!data.id) {
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: "Tax Group ID is missing",
                });
                return;
            }

            const resp = await deleteTaxGroup(data.id).unwrap();
            
            if (resp) {
                // Check if this is the last record on the current page and we're not on the first page
                // If current page has only 1 record (the one being deleted) and offset > 0, go back one page
                const isLastRecordOnPage = taxGrouptableData.length === 1;
                const isNotFirstPage = searchCriteria.offset > 0;
                
                if (isLastRecordOnPage && isNotFirstPage) {
                    // Calculate previous page offset
                    const previousPageOffset = Math.max(0, searchCriteria.offset - searchCriteria.limit);
                    
                    setSearchCriteria((prev) => ({
                        ...prev,
                        offset: previousPageOffset,
                    }));
                    
                    // Update filterData to keep it in sync
                    setFilterData((prev) => ({
                        ...prev,
                        offset: previousPageOffset,
                    }));
                }
                
                // Clear search input if exists
                setInputValue("");
                
                // Refetch data to update the table
                refetch();
                
                toast({
                    variant: "success",
                    title: "Tax Group Deleted Successfully",
                });
            }
        } catch (error: any) {
            const errorMessage = 
                error?.data?.message || 
                error?.message || 
                "Failed to delete tax group. Please try again.";
            
            toast({
                variant: "destructive",
                title: "Error",
                description: errorMessage,
            });
        }
    };

    const actionsColumn: ColumnDef<TaxGroup> = {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
            <DataTableRowActions
                access={tax_group}
                data={row.original}
                refetch={refetch}
                handleEdit={handleEditTaxGroup}
                handleDelete={handleDeleteTaxGroup}
            />
        ),
    };

    const columns: ColumnDef<TaxGroup>[] = [
        {
            accessorKey: "name",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Tax Group Name"
                    sortKey="name"
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => {
                return (
                    <div className="w-fit">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <p className="capitalize cursor-pointer">
                                        <span>
                                            {displayValue(
                                                `${row.original.name}`.length > 20
                                                    ? `${row.original.name}`.substring(0, 20) + "..."
                                                    : `${row.original.name}`
                                            )}
                                        </span>
                                    </p>
                                </TooltipTrigger>
                                <TooltipContent className="mr-[16.5rem]">
                                    <p className="captilaize text-sm">
                                        {displayValue(`${row?.original?.name}`)}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                );
            },
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "taxes",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Sale Taxes"
                    sortKey="name"
                    sortable={false}
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex flex-wrap gap-2">
                        {row.original.taxes?.length ? (
                            row.original.taxes.map((tax: SaleTax) => (
                                <span
                                    key={tax.id}
                                    className="inline-block rounded-full bg-blue-100 text-blue-800 px-3 py-1 text-xs font-medium"
                                >
                                    {`${tax.name} (${tax.percentage}%)`}
                                </span>
                            ))
                        ) : (
                            <span className="text-gray-400 text-xs">No taxes</span>
                        )}
                    </div>
                );
            },
            enableSorting: false,
            enableHiding: false,
        },

        ...(tax_group !== "read" ? [actionsColumn] : []),
    ];

    const table = useReactTable({
        data: taxGrouptableData as TaxGroup[],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });


    const handleAddTaxGroup = () => {
        setData(undefined);
        setAction("add");
        setIsDialogOpen(true);
    };

    const handleFilterChange = (
        field: string,
        value: string | number | number[] | React.ChangeEvent<HTMLInputElement>
    ) => {
        let newValue;

        // Check if the value is coming from an input event or passed directly
        if (typeof value === "object" && "target" in value) {
            newValue = value.target.value;
        } else {
            newValue = value;
        }

        if (field === "discount_percentage") {
            newValue = Math.min(Number(newValue), 100); // Capping the discount percentage to 100
        }

        // Update the filter state based on field and value
        setFilterData((prev) => ({
            ...prev,
            [field]: newValue,
        }));
    };



    const filterDisplay = [
        {
            type: "multiselect",
            name: "sale_tax_ids",
            label: "Sales Tax",
            options: salesTaxOptions,
            function: (value: number) => handleFilterChange("sale_tax_ids", value),
        },

    ];

    const totalRecords = taxGroupData?.filtered_counts || 0;
    const {
        handleLimitChange,
        handleNextPage,
        handlePrevPage,
        handleFirstPage,
        handleLastPage,
        isLastPage,
    } = usePagination({
        totalRecords,
        searchCriteria,
        setSearchCriteria,
    });

    return (
        <div className="w-full space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
                {/* Table Filter component */}
                <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />

                {/* Buttons Container */}
                <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
                    {tax_group !== "read" && (
                        <Button
                            className="bg-primary text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
                            onClick={handleAddTaxGroup}
                        >
                            <PlusIcon className="size-4" />
                            Create New
                        </Button>
                    )}
                    <button
                        className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
                        onClick={() => setOpenFilter(true)}
                    >
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <i
                                        className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                                    ></i>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>click to apply filter</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </button>
                </div>
            </div>

            <div className="rounded-none border border-border z-0">
                <ScrollArea className="w-full relative" hideScrollToTop={true}>
                    <ScrollBar
                        orientation="horizontal"
                        className="relative cursor-grab h-2"
                    />
                    {/* <Table className="w-full overflow-x-scroll"> */}
                    <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
                        {" "}
                        {/* //overflow-y-auto scrollbar-thin */}
                        <TableHeader className="bg-secondary">
                            {table?.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        <div className="flex space-x-2 justify-center items-center bg-white ">
                                            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                            <div className="size-3 bg-black rounded-full animate-bounce"></div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : table.getRowModel().rows.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                        onClick={(e) => {
                                            if (tax_group !== "read" && e.target instanceof Element) {
                                                const target = e.target as Element;
                                                if (!target.closest('[role="checkbox"]') && (e.target === e.currentTarget || target.closest('td'))) {
                                                    handleEditTaxGroup(row.original);
                                                }
                                            }
                                        }}
                                        className={`${tax_group !== "read" ? "cursor-pointer hover:bg-secondary" : ""}`}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        No records found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </ScrollArea>
            </div>

            {/* pagination */}
            {taxGrouptableData && taxGrouptableData?.length > 0 ? (
                <Pagination
                    limit={searchCriteria.limit}
                    offset={searchCriteria.offset}
                    totalItems={totalRecords}
                    onLimitChange={handleLimitChange}
                    onNextPage={handleNextPage}
                    onPrevPage={handlePrevPage}
                    onFirstPage={handleFirstPage}
                    onLastPage={handleLastPage}
                    isLastPage={isLastPage}
                />
            ) : <></>}


            <TableFilters
                isOpen={openFilter}
                setOpen={setOpenFilter}
                initialValue={initialValues}
                filterData={filterData}
                setFilter={setFilterData}
                setSearchCriteria={setSearchCriteria}
                filterDisplay={filterDisplay}
                setInputValue={setInputValue}
            />


            <TaxGroupForm
                isOpen={isDialogOpen}
                setOpen={setIsDialogOpen}
                data={data}
                setData={setData}
                action={action}
                setAction={setAction}
                refetch={refetch}
            />

        </div>
    );
}


